import { useTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, { useMemo, useCallback, useRef } from 'react'

import { DeckOverlay } from 'components/common/map/webgl/DeckOverlay'

import { buildContainerMarkerLayer } from './ContainerMarkerLayer'
import { buildContainerTetherLayer } from './ContainerTetherLayer'
import { useContainerIconsReady } from './generators/containerIconRaster'

// Lines tying each container back to its stop. Returned separately from the
// marker layer so callers can order them underneath — a tether drawn over the
// pills reads as a strikethrough.
export function useDeckContainerTetherLayers({ containerFeatures = [], stopFeatures = [], isActive = true } = {}) {
  const theme = useTheme()

  return useMemo(() => {
    if (!isActive) return []
    const layer = buildContainerTetherLayer({ containerFeatures, stopFeatures, theme })
    return layer ? [layer] : []
  }, [containerFeatures, stopFeatures, isActive, theme])
}

// Hook form: returns the deck.gl layer (or []) without mounting a DeckOverlay.
// Lets callers compose container markers into a shared overlay alongside other
// layers — see useBulkDeckMarkerLayers for the same pattern.
//
// Hover resolution: `onHover` is invoked with the raw deck.gl `info` object so
// the caller can convert to client coords via `pickInfoToClientXY(map, info)`
// or any equivalent. The component wrapper below adds a wrapper-rect fallback
// for legacy consumers that don't have `map` in scope.
export function useDeckContainerMarkerLayers({ features = [], onClick, onHover } = {}) {
  const theme = useTheme()
  // Re-render when the type SVGs finish rasterizing so the initial paint isn't empty.
  useContainerIconsReady()

  const handleClick = useCallback(
    info => {
      if (info?.object?.feature && onClick) onClick(info.object.feature)
    },
    [onClick]
  )

  const handleHover = useCallback(
    info => {
      if (!onHover) return
      if (info?.object?.feature) onHover(info.object.feature, info)
      else onHover(null, null)
    },
    [onHover]
  )

  return useMemo(
    () =>
      features.length > 0
        ? [buildContainerMarkerLayer({ features, theme, onClick: handleClick, onHover: onHover ? handleHover : undefined })]
        : [],
    [features, theme, handleClick, handleHover, onHover]
  )
}

const DeckContainerMarkerRenderer = ({ features = [], onClick, onHover, pane = 'floatPane', zIndex = 5 }) => {
  // Wrapper rect re-queried on every hover so viewport→page coords stay correct across scrolls.
  const wrapperRef = useRef(null)

  const wrappedHover = useCallback(
    (feature, info) => {
      if (!onHover) return
      if (!feature) {
        onHover(null, null)
        return
      }
      const rect = wrapperRef.current?.getBoundingClientRect()
      const clientX = (info?.x ?? 0) + (rect?.left ?? 0)
      const clientY = (info?.y ?? 0) + (rect?.top ?? 0)
      onHover(feature, { clientX, clientY })
    },
    [onHover]
  )

  const layers = useDeckContainerMarkerLayers({ features, onClick, onHover: onHover ? wrappedHover : undefined })

  // Default floatPane/z5 sits containers above native polylines but under the
  // bulk stop markers (also floatPane, z10). Surfaces whose stop markers live in
  // a lower pane (e.g. the V2 cluster map, overlayLayer z3) override pane/zIndex
  // so containers still render BELOW their stops.
  const overlayStyle = useMemo(() => ({ zIndex }), [zIndex])
  return (
    <div ref={wrapperRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <DeckOverlay layers={layers} pane={pane} style={overlayStyle} />
    </div>
  )
}

DeckContainerMarkerRenderer.propTypes = {
  features: PropTypes.array,
  onClick: PropTypes.func,
  onHover: PropTypes.func,
  pane: PropTypes.oneOf(['floatPane', 'overlayMouseTarget', 'overlayLayer']),
  zIndex: PropTypes.number,
}

export default DeckContainerMarkerRenderer
